import _ from "lodash";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { PrimaryButton } from "../components/Buttons";
import { DateInput, TextInput } from "../components/Inputs";
import { PageHeader } from "../components/Layouts";
import { PrimaryTable, StaticFilterSection } from "../components/Tables";
import { useApi, useReportDownloadHelpers } from "../hooks";
import constants from "../utils/constants";
import dateHelpers from "../utils/dateHelpers";

const RegistrationTable = ({ filters, data }) => {
  return (
    <>
      <PrimaryTable
        totalCount={data?.totalCount}
        displayCount={constants.DISPLAY_PER_PAGE}
        header={
          <PrimaryTable.Header>
            <PrimaryTable.HeaderColumn display="Week Start On" />
            <PrimaryTable.HeaderColumn display="Patron Count" />
            <PrimaryTable.HeaderColumn display="Business Count" />
            <PrimaryTable.HeaderColumn display="Organization Count" />
          </PrimaryTable.Header>
        }
      >
        {data &&
          _.map(data, (r, index) => (
            <PrimaryTable.Row key={`${r.weekStartOn}-${index}`} index={index}>
              <PrimaryTable.Cell>{r.weekStartOn}</PrimaryTable.Cell>
              <PrimaryTable.Cell>{r.patronCount}</PrimaryTable.Cell>
              <PrimaryTable.Cell>{r.businessCount}</PrimaryTable.Cell>
              <PrimaryTable.Cell>{r.orgCount}</PrimaryTable.Cell>
            </PrimaryTable.Row>
          ))}
      </PrimaryTable>
    </>
  );
};

const RegistrationFilter = ({ setFilters, filters, onFiltersChange }) => {
  const handleCurrentLocation = () => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          onFiltersChange({
            ...filters,
            Latitude: latitude,
            Longitude: longitude,
          });
        },
        (error) => {
          console.error(error);
        }
      );
    } else {
      console.warn("geolocation not available");
    }
  };

  useEffect(handleCurrentLocation, []);

  return (
    <StaticFilterSection filters={filters} showFilterCount={false}>
      <div className="grid grid-cols-12 gap-4 w-full p-4">
        <div className="col-span-2 min-w-full">
          <TextInput
            label="Latitude"
            name="Latitude"
            placeholder="Enter latitude"
            value={filters.Latitude || ""}
            onChange={(e) =>
              onFiltersChange({
                Latitude:
                  e.target.value === "" ? null : parseFloat(e.target.value),
              })
            }
          />
          <button
            onClick={handleCurrentLocation}
            className="text-sm text-left text-blue-700 hover:text-gray-800 font-semibold py-1 ps-1 inline-flex"
          >
            Use Current Location
          </button>
        </div>
        <div className="col-span-2 min-w-full">
          <TextInput
            label="Longitude"
            name="Longitude"
            placeholder="Enter longitude"
            value={filters.Longitude || ""}
            onChange={(e) =>
              onFiltersChange({
                Longitude:
                  e.target.value === "" ? null : parseFloat(e.target.value),
              })
            }
          />
        </div>
        <div className="col-span-1 min-w-full">
          <TextInput
            label="Radius Miles"
            placeholder="Radius Miles"
            value={filters.RadiusMiles}
            onChange={(e) =>
              onFiltersChange({
                ...filters,
                RadiusMiles: e.target.value,
              })
            }
          />
        </div>
        <div className="col-span-2 min-w-full">
          <DateInput
            label="Date From"
            placeholder="Date From"
            required={true}
            value={filters.DateFrom}
            onChange={(e) =>
              onFiltersChange({
                ...filters,
                DateFrom: e.target.value,
              })
            }
          />
        </div>
        <div className="col-span-2 min-w-full">
          <DateInput
            label="Date Through"
            placeholder="Date Through"
            required={true}
            value={filters.DateThrough}
            onChange={(e) =>
              onFiltersChange({
                ...filters,
                DateThrough: e.target.value,
              })
            }
          />
        </div>
        <div className="col-span-3 min-w-full">
          <PrimaryButton 
            text="Reset"
            backgroundColor="gray-500"
            className="float-right mt-5"
            small
            onClick={() => setFilters({})}                    
          />
        </div>
      </div>
    </StaticFilterSection>
  );
};

export default function RegistrationReport() {
  const { downloadFileFromApiPost } = useReportDownloadHelpers();
  const { post } = useApi();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [tainted, setTainted] = useState(true);
  const [filters, setFilters] = useState({
    Latitude: null,
    Longitude: null,
    RadiusMiles: 10,
    DateFrom: moment().subtract(1, "month").format(dateHelpers.YMD),
    DateThrough: moment().format(dateHelpers.YMD),
  });

  const onFiltersChange = (updatedFilters) => {
    setTainted(true);
    setFilters((prevFilters) => ({ ...prevFilters, ...updatedFilters }));
  };

  function getValidationWarnings() {
    let warnings = [];
    if (!dateHelpers.verifyDate(filters.DateThrough)) {
      warnings.push("Provide a date through.");
    }
    return warnings;
  }

  function validate() {
    const warnings = getValidationWarnings();
    if (warnings.length) {
      alert(warnings.join(", "), "Please try again", "error");
      return false;
    }
    return true;
  }

  function buildPayload() {
    return {
      DateFrom: filters.DateFrom,
      DateThrough: filters.DateThrough,
      RadiusMiles: filters.RadiusMiles,
      ReferenceLocation: {
        Latitude: filters.Latitude || 0,
        Longitude: filters.Longitude || 0,
      },
    };
  }

  const downloadReport = async () => {
    if (!validate()) return;
    downloadFileFromApiPost(
      "/report/GenerateRegistrationReport",
      buildPayload(),
      "RegistrationReport.xlsx"
    );
  };

  const executeReport = () => {
    setIsLoading(true);
    post("report/RegistrationReport", buildPayload())
      .then((res) => {
        setData(res);
        setTainted(false);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <div className="w-100 h-auto">
      <div className="flex justify-between">
        <PageHeader title="Registration Report" />
        <div className="flex flex-row justify-between">
          <PrimaryButton
            disabled={!tainted}
            onClick={executeReport}
            text={"Run Report"}
            className={"w-1/3 mr-2"}
          />
          <PrimaryButton
            onClick={downloadReport}
            text={"Download Report"}
            className={"w-1/3"}
          />
        </div>
      </div>

      <div className="w-full h-1/4 justify-center">
        <RegistrationFilter
          setFilters={setFilters}
          filters={filters}
          onFiltersChange={onFiltersChange}
        />
      </div>

      {data && !tainted && !isLoading && (
        <RegistrationTable
          filters={filters}
          onFiltersChange={onFiltersChange}
          data={data}
        />
      )}
    </div>
  );
}
